import React from "react";
import { useLang } from "../LanguageContext";
import { LanguageToggle } from "./LanguageToggle";
import {
  Phone,
  MessageCircle,
  Mail,
  Globe,
  ShieldCheck,
  Users,
  Award,
  Zap,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Landmark,
  Wallet,
  FileCheck,
  Plane,
  GraduationCap,
  Building2,
  Calculator,
  BadgePoundSterling,
  ReceiptText,
  BookOpenCheck,
  Briefcase,
  Cpu,
  Lock,
} from "lucide-react";

export const Logo = ({ variant = "dark", size = 44 }) => {
  const color = variant === "dark" ? "#002B7F" : "#FFFFFF";
  const sub = variant === "dark" ? "#4A4A4A" : "rgba(255,255,255,0.75)";
  return (
    <a href="/" className="flex items-center gap-3 group" data-testid="brand-logo">
      <img
        src="/brand/marioway-logo.png"
        alt="Marioway Benefits Consultancy LTD"
        width={size}
        height={size}
        style={{ width: size, height: size, objectFit: "contain", borderRadius: "50%" }}
        className="shrink-0"
      />
      <span className="flex flex-col leading-tight">
        <span className="font-display text-[18px]" style={{ color }}>
          Marioway
        </span>
        <span className="hidden sm:block text-[11px] tracking-[0.18em] uppercase" style={{ color: sub, fontWeight: 600 }}>
          Benefits Consultancy
        </span>
      </span>
    </a>
  );
};

export const TopBar = () => (
  <div className="navy-gradient text-white text-[13px]">
    <div className="container-x flex flex-wrap items-center justify-between py-2 gap-2">
      <div className="flex items-center gap-5 opacity-90">
        <span className="inline-flex items-center gap-2">
          <Lock size={13} className="text-[#FF8C42]" /> Independent Administrative Support
        </span>
        <span className="hidden md:inline-flex items-center gap-2">
          <Globe size={13} className="text-[#FF8C42]" /> Supporting you in the UK
        </span>
        <span className="hidden lg:inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.18em] text-white/80">
          <span className="px-1.5 py-0.5 rounded bg-white/10">EN</span>
          <span className="px-1.5 py-0.5 rounded bg-white/10">RO</span>
          <span className="px-1.5 py-0.5 rounded bg-white/10">ES</span>
        </span>
      </div>
      <div className="flex items-center gap-5">
        <a href="tel:07383513170" className="inline-flex items-center gap-2 hover:text-[#FF8C42] transition-colors" data-testid="topbar-phone">
          <Phone size={13} /> 07383513170
        </a>
        <a href="mailto:info@mariowayltd.com" className="hidden sm:inline-flex items-center gap-2 hover:text-[#FF8C42] transition-colors" data-testid="topbar-email">
          <Mail size={13} /> info@mariowayltd.com
        </a>
      </div>
    </div>
  </div>
);

export const Nav = () => {
  const { t } = useLang();
  const links = [
    { label: t("nav.services"), href: "/#services" },
    { label: t("nav.showcase"), href: "/#showcase" },
    { label: t("nav.payments"), href: "/#payments" },
    { label: t("nav.about"), href: "/#about" },
    { label: t("nav.contact"), href: "/#contact" },
  ];
  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-[#E6EAF0]">
      <div className="container-x flex items-center justify-between gap-3 py-4">
        <Logo />
        <nav className="hidden md:flex items-center gap-6">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-[14px] font-semibold text-[#1A1A1A] hover:text-[#F37021] transition-colors"
              data-testid={`nav-${l.href.replace('/#', '')}`}
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2 shrink-0">
          <LanguageToggle />
          <a
            href="https://wa.me/447383513170"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-orange text-[14px] px-3.5 sm:px-4 py-2.5 whitespace-nowrap"
            data-testid="nav-cta-whatsapp"
            aria-label="WhatsApp"
          >
            <MessageCircle size={16} /> <span className="hidden sm:inline">{t("nav.whatsapp")}</span>
          </a>
        </div>
      </div>
    </header>
  );
};

export const Hero = () => {
  const { t } = useLang();
  return (
  <section id="top" className="relative overflow-hidden navy-gradient-soft grain text-white">
    {/* Water + Fire fusion orbs (Marioway brand element) */}
    <div className="hero-orb" style={{ width: 480, height: 480, background: "radial-gradient(circle, #F37021 0%, transparent 60%)", top: -140, right: -60, opacity: 0.55 }} />
    <div className="hero-orb" style={{ width: 380, height: 380, background: "radial-gradient(circle, #1F4E79 0%, transparent 60%)", top: 60, right: 100, opacity: 0.6 }} />
    <div className="hero-orb" style={{ width: 360, height: 360, background: "radial-gradient(circle, #CE1126 0%, transparent 60%)", bottom: -120, left: -60, opacity: 0.35 }} />
    <div className="hero-orb" style={{ width: 280, height: 280, background: "radial-gradient(circle, #FCD116 0%, transparent 60%)", bottom: 80, left: 200, opacity: 0.18 }} />

    <div className="container-x relative pt-20 pb-28 md:pt-28 md:pb-36">
      <div className="tricolor-bar absolute top-0 left-0 right-0" />
      <div className="grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <span className="eyebrow eyebrow-on-navy rise" data-testid="hero-eyebrow">
            <ShieldCheck size={12} /> Trusted UK Consultancy
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] rise-2" data-testid="hero-headline">
            Your support. <span className="text-[#F37021]">Our priority.</span>
          </h1>
          <p className="mt-6 text-white/80 text-lg max-w-xl rise-3" data-testid="hero-subtext">
            Marioway Benefits Consultancy Ltd provides independent administrative, welfare support, accounting administration, education support and business support services to individuals, families and small businesses across the UK. Multilingual support in English, Romanian and Spanish — clear, calm, professional.
          </p>

          <div className="mt-6 inline-flex items-start gap-3 rounded-2xl border border-white/15 bg-white/[0.07] backdrop-blur px-4 py-3 rise-3" data-testid="hero-credential">
            <span className="mt-0.5 inline-grid place-items-center w-9 h-9 rounded-full bg-[#FCD116]/15 text-[#FCD116]"><GraduationCap size={18} /></span>
            <span className="leading-tight">
              <span className="block font-display text-[16px] text-white">Florin-Marius Bran</span>
              <span className="block text-[12.5px] text-white/85">BSc Accounting and Financial Management</span>
              <span className="block text-[11.5px] text-white/60">Canterbury Christ Church University · 2026 · Founder &amp; Director</span>
            </span>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3 rise-4">
            <a href="tel:07383513170" className="btn-orange" data-testid="hero-cta-call">
              <Phone size={18} /> Call 07383513170
            </a>
            <a
              href="https://wa.me/447383513170"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-navy"
              data-testid="hero-cta-whatsapp"
            >
              <MessageCircle size={18} /> WhatsApp
            </a>
            <a href="/#contact" className="btn-ghost" style={{ color: "#fff", borderColor: "rgba(255,255,255,0.35)" }} data-testid="hero-cta-free">
              Free initial consultation <ArrowRight size={16} />
            </a>
          </div>

        </div>

        <div className="md:col-span-5 relative">
          <div className="relative rise-3">
            <div className="soft-card p-6 md:p-7 text-[#1A1A1A]" style={{ background: "#FFFFFF" }}>
              <div className="flex items-center gap-3">
                <span className="icon-tile orange"><Briefcase size={22} /></span>
                <div>
                  <div className="font-display text-xl">Free initial 15-minute consultation</div>
                  <div className="text-[13px] text-[#4A4A4A]">No obligation. Confidential.</div>
                </div>
              </div>
              <div className="hair my-5" />
              <ul className="space-y-3 text-[14px]">
                {[
                  "Welfare Benefits & Administrative Support",
                  "Immigration Document & Digital Status Support",
                  "Education & Student Support",
                  "Accounting, Bookkeeping & Business Administration",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-[#F37021]" /> {t}
                  </li>
                ))}
              </ul>
              <a href="/#services" className="mt-6 inline-flex items-center gap-2 text-[#002B7F] font-bold hover:text-[#F37021] transition-colors" data-testid="hero-card-link">
                See all 10 core services <ArrowUpRight size={16} />
              </a>
            </div>

            <div className="float-badge" style={{ top: -18, right: 18 }} data-testid="hero-badge-trust">
              <span className="inline-flex items-center gap-2"><Award size={14} className="text-[#FF8C42]" /> Companies House Registered</span>
            </div>
            <div className="float-badge" style={{ bottom: -18, left: 18 }} data-testid="hero-badge-secure">
              <span className="inline-flex items-center gap-2"><Lock size={14} className="text-[#FF8C42]" /> Secure Payments</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};

export const TrustBar = () => {
  const items = [
    { icon: Lock, label: "Confidential Service" },
    { icon: Users, label: "Clear Guidance" },
    { icon: Award, label: "Professional Support" },
    { icon: Zap, label: "Practical Solutions" },
  ];
  return (
    <section className="bg-[#F5F7FA] border-y border-[#E6EAF0]">
      <div className="container-x grid grid-cols-2 md:grid-cols-4 gap-6 py-8">
        {items.map(({ icon: Icon, label }) => (
          <div key={label} className="flex items-center gap-3" data-testid={`trust-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <span className="icon-tile"><Icon size={22} /></span>
            <span className="font-semibold text-[#002B7F]">{label}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

const SERVICES = [
  {
    title: "EU Settlement Scheme (EUSS) — Administrative Support",
    subtitle: "Document & Digital Status Support",
    image: "/brand/services/euss.png",
    accent: "navy",
    description:
      "Administrative assistance with EU Settlement Scheme documentation and digital processes where regulated immigration advice is not required.",
    bullets: [
      { h: "Document Organisation", s: "We help you gather and organise the documents requested by the official process." },
      { h: "GOV.UK Digital Services", s: "Assistance accessing your UKVI account and official online services." },
      { h: "Share Code", s: "Help obtaining and using your Share Code to prove your status." },
      { h: "Status Update Assistance", s: "Administrative help updating details such as passport or address in your online account." },
      { h: "Ongoing Assistance", s: "Practical administrative support whenever you need it." },
    ],
    foot: "General administrative assistance only — not regulated immigration advice. Where regulated advice is required, please consult an appropriately authorised immigration adviser or solicitor.",
  },
  {
    title: "Universal Credit Administrative Support",
    subtitle: "Professional · Independent · Confidential",
    image: "/brand/services/universal-credit.png",
    accent: "orange",
    description:
      "Practical administrative support to help you understand the Universal Credit process and prepare your information and documentation.",
    bullets: [
      { h: "New Claims", s: "Support completing a new Universal Credit application." },
      { h: "Change of Circumstances", s: "We help you report and document any changes." },
      { h: "Payment Statements", s: "We help you understand your payment statements and journal." },
      { h: "Journal Messages", s: "Assistance drafting journal messages and preparing for appointments." },
    ],
    foot: "Eligibility and entitlement decisions are made exclusively by the Department for Work and Pensions.",
  },
  {
    title: "ETA Application Administrative Assistance",
    subtitle: "Clear. Organised. Confidential.",
    image: "/brand/services/eta.png",
    accent: "navy",
    description:
      "Administrative assistance with the Electronic Travel Authorisation (ETA) online process where regulated immigration advice is not required.",
    bullets: [
      { h: "Understanding the Process", s: "We explain the official steps and what information is requested." },
      { h: "Form Completion Assistance", s: "Help entering your information accurately in the official application." },
      { h: "Document Organisation", s: "We help you prepare the correct information and documents." },
      { h: "Secure & Confidential", s: "Your information is handled with care and confidentiality." },
      { h: "Clear Communication", s: "Simple, step-by-step support in your language." },
    ],
    foot: "Decisions on ETA applications are made solely by the Home Office. We do not guarantee approval.",
  },
  {
    title: "Council Tax Support",
    subtitle: "Local council applications",
    image: "/brand/services/council-tax.png",
    accent: "green",
    description:
      "Administrative support with Council Tax Support / Reduction applications to your local council, including Leeds City Council.",
    bullets: [
      { h: "Application Support", s: "We help you complete your Council Tax Support application." },
      { h: "Understanding the Criteria", s: "We explain the information the council asks for and how the process works." },
      { h: "Help with Documents", s: "We help you gather and prepare the correct documents." },
      { h: "Accurate Completion", s: "We help you complete the application accurately." },
      { h: "Ongoing Assistance", s: "Support with reviews, changes and renewals." },
    ],
    foot: "Local knowledge. Practical support. Decisions remain with the council.",
  },
  {
    title: "Student Finance England Support",
    subtitle: "Your Education. Your Future.",
    image: "/brand/services/student-finance.png",
    accent: "purple",
    description:
      "Administrative support with Student Finance England applications, evidence preparation, application forms and related documentation.",
    bullets: [
      { h: "New Applications", s: "We help you complete your Student Finance application." },
      { h: "Maintenance Loans", s: "Information and support for maintenance loan applications." },
      { h: "Tuition Fee Loans", s: "Information about tuition fee loans and the application process." },
      { h: "Repayment Information", s: "Clear information about how repayments work." },
      { h: "Ongoing Support", s: "We're here to help you at every stage of your student journey." },
    ],
    foot: "We make the process simple and clear. Funding decisions are made by Student Finance England.",
  },
  {
    title: "University Application Administration",
    subtitle: "Student application support",
    image: "/brand/services/gbs.png",
    accent: "yellow",
    description:
      "Administrative support for students applying to universities and higher-education providers, helping you organise your application and documents.",
    bullets: [
      { h: "Course Information", s: "Help researching courses and entry requirements." },
      { h: "Application Administration", s: "Step-by-step support completing your applications." },
      { h: "Document Preparation", s: "We help you prepare and check the required documents." },
      { h: "Enrolment Administration", s: "Support with offers, enrolment paperwork and deadlines." },
      { h: "Ongoing Student Support", s: "We're here to support you after you start your studies." },
    ],
    foot: "Four years' experience as a Student Ambassador at GBS Leeds, supporting students of CCCU, Oxford Brookes, Bath Spa, Pearson and University of Suffolk. References to institutions do not imply endorsement or partnership.",
  },
  {
    title: "Smart Tools / Business Support",
    subtitle: "Modern tools for modern business",
    image: "/brand/services/smart-tools.png",
    accent: "orange",
    description:
      "Practical business administration and digital tools to help your operation run smoothly and stay organised.",
    bullets: [
      { h: "Business Setup", s: "Administrative support setting up and structuring your business." },
      { h: "Digital Tools", s: "Modern, efficient digital tools for everyday operations." },
      { h: "Organised Records", s: "Keep your records organised, complete and up to date." },
      { h: "Ongoing Support", s: "Continuous support as your business evolves." },
    ],
    foot: "Smart Tools · Better Organisation · Efficiency & Growth · Support When You Need It.",
  },
  {
    title: "Payroll Administration",
    subtitle: "Professional Payroll Administration",
    image: "/brand/services/payroll.png",
    accent: "navy",
    description: "Accurate. Organised. On Time.",
    bullets: [
      { h: "PAYE Administration", s: "Payroll processed in line with HMRC reporting requirements." },
      { h: "On-Time Processing", s: "Reliable, on-time payroll administration." },
      { h: "Secure & Confidential", s: "Your data handled with strict confidentiality." },
      { h: "Payslips & Reports", s: "Clear payslips and detailed reports." },
      { h: "Auto Enrolment", s: "Pension auto-enrolment administration." },
      { h: "Modern Digital Tools", s: "Efficient, digital payroll workflows." },
    ],
    foot: "Let us take care of your payroll administration, so you can focus on your business.",
  },
  {
    title: "Self Assessment Administrative Support & Digital Tax",
    subtitle: "Organised records. Clear deadlines.",
    image: "/brand/services/self-employed.png",
    accent: "orange",
    description:
      "Administrative support with Self Assessment documentation and Making Tax Digital record-keeping for self-employed clients.",
    bullets: [
      { h: "Self Assessment Support", s: "Documentation prepared accurately and on time." },
      { h: "Expense Records", s: "We help you organise records for the expenses relevant to you." },
      { h: "HMRC Administrative Support", s: "Help with HMRC correspondence and online accounts." },
      { h: "Digital Tax (MTD Ready)", s: "Record-keeping organised for Making Tax Digital." },
      { h: "Reliable Service", s: "Clear timelines you can count on." },
    ],
    foot: "We do not guarantee tax refunds, HMRC decisions or specific financial outcomes.",
  },
  {
    title: "Bookkeeping",
    subtitle: "Organised books. Stronger business.",
    image: "/brand/services/bookkeeping.png",
    accent: "red",
    description:
      "Professional bookkeeping support to keep your financial records clean, accurate and ready for tax time.",
    bullets: [
      { h: "Daily transaction management", s: "Your transactions tracked and categorised." },
      { h: "Bank reconciliation", s: "Accurate bank reconciliation every month." },
      { h: "Expense tracking", s: "Clear, organised expense tracking." },
      { h: "Financial reports", s: "Reports that give you real insight into your business." },
    ],
    foot: "Accurate & Reliable · Save Time · Better Financial Insight · Focus on Your Business.",
  },
  {
    title: "Tax Return Documentation Support",
    subtitle: "Organised. Accurate. On time.",
    image: "/brand/services/tax-return.png",
    accent: "orange",
    description:
      "Support preparing tax return documentation for individuals and small businesses — organised, confidential, on time.",
    bullets: [
      { h: "Accurate preparation", s: "Documentation prepared carefully and checked." },
      { h: "Allowable expenses", s: "We help you organise records for the allowable expenses relevant to you." },
      { h: "Secure process", s: "Confidential handling of your information." },
      { h: "Personal & business", s: "Both personal and small-business documentation covered." },
    ],
    foot: "We do not guarantee tax refunds, HMRC decisions or specific financial outcomes.",
  },
];

export const Services = () => (
  <section id="showcase" className="section">
    <div className="container-x">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div className="max-w-2xl">
          <span className="eyebrow"><FileCheck size={12} /> Service Showcase</span>
          <h2 className="font-display text-4xl sm:text-5xl mt-4 text-[#002B7F] leading-[1.05]">
            Detailed services <span className="text-[#F37021]">at a glance.</span>
          </h2>
          <p className="mt-4 text-[#4A4A4A] text-base max-w-xl">
            Each card explains what we provide — clear steps, what is included, and a single point of contact. Outcome decisions remain with the relevant authority.
          </p>
        </div>
        <a href="/#contact" className="btn-ghost self-start md:self-auto" data-testid="services-cta">
          Book a free initial chat <ArrowRight size={16} />
        </a>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {SERVICES.map((svc, idx) => (
          <article key={svc.title} className={`poster-card accent-${svc.accent}`} data-testid={`service-card-${idx}`}>
            {svc.image ? (
              <img src={`${svc.image}?v=3`} alt={svc.title} className="poster-img" loading="lazy" />
            ) : svc.swirl ? (
              <div className="swirl-cover">
                <div className="swirl-content">
                  <div className="swirl-icons">
                    {svc.swirlIcons === "tech" ? (
                      <>
                        <Cpu size={20} className="text-white" />
                        <Briefcase size={20} className="text-[#FCD116]" />
                        <ShieldCheck size={20} className="text-white" />
                      </>
                    ) : (
                      <>
                        <Briefcase size={20} className="text-white" />
                        <Calculator size={20} className="text-[#FCD116]" />
                        <BadgePoundSterling size={20} className="text-white" />
                      </>
                    )}
                  </div>
                  <div className="swirl-title mt-4">{svc.swirlTitle}</div>
                  <div className="swirl-sub">{svc.swirlSub}</div>
                </div>
              </div>
            ) : (
              <div className="poster-img" style={{ background: "linear-gradient(135deg, #002B7F, #1F4E79)", display: "flex", alignItems: "center", justifyContent: "center", color: "#FCD116", fontFamily: "Fraunces, serif", fontSize: 28, padding: 24, textAlign: "center" }}>
                {svc.title}
              </div>
            )}
            <div className="poster-body">
              <span className="tag-chip">{svc.subtitle}</span>
              <h3 className="font-display text-[22px] mt-3 leading-tight" style={{ color: "var(--accent)" }}>
                {svc.title}
              </h3>
              <p className="mt-2 text-[14px] text-[#4A4A4A] leading-relaxed">{svc.description}</p>

              <ul className="mt-5 space-y-3">
                {svc.bullets.map((b) => (
                  <li key={b.h} className="svc-bullet">
                    <span className="dot"><CheckCircle2 size={12} /></span>
                    <span>
                      <span className="font-bold text-[#002B7F]">{b.h}.</span>{" "}
                      <span className="text-[#4A4A4A]">{b.s}</span>
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-5 pt-4 border-t border-[#E6EAF0] flex items-start gap-2">
                <span className="text-[12.5px] italic" style={{ color: "var(--accent)" }}>
                  {svc.foot}
                </span>
              </div>

              <div className="mt-5 flex items-center justify-between">
                <span className="text-[12px] uppercase tracking-[0.14em] text-[#4A4A4A] font-bold">
                  Call · WhatsApp
                </span>
                <a
                  href="/#contact"
                  className="inline-flex items-center gap-1.5 font-bold hover:opacity-80 transition-opacity"
                  style={{ color: "var(--accent)" }}
                  data-testid={`service-link-${idx}`}
                >
                  Enquire <ArrowUpRight size={14} />
                </a>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const PAYMENT_METHODS = [
  { name: "PayPal", slug: "paypal", color: "003087" },
  { name: "Stripe", slug: "stripe", color: "635BFF" },
  { name: "Square", slug: "square", color: "3E4348" },
  { name: "Visa", slug: "visa", color: "1A1F71" },
  { name: "Mastercard", slug: "mastercard", color: "EB001B" },
  { name: "Apple Pay", slug: "applepay", color: "000000" },
  { name: "Google Pay", slug: "googlepay", color: "4285F4" },
  { name: "Zilch", slug: null, fallback: "6E3CFF" },
  { name: "Klarna", slug: "klarna", color: "FFA8CD" },
  { name: "Clearpay", slug: null, fallback: "B2FCE4" },
  { name: "Crypto Bitcoin", slug: "bitcoin", color: "F7931A" },
];

export const Payments = () => (
  <section id="payments" className="section bg-[#F5F7FA]">
    <div className="container-x">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <span className="eyebrow"><Wallet size={12} /> WE ACCEPT</span>
        <h2 className="font-display text-4xl sm:text-5xl mt-4 text-[#002B7F]">
          Secure Payments.<br />
          <span className="text-[#F37021]">Trusted Service.</span>
        </h2>
        <p className="mt-4 text-[#4A4A4A]">
          Pay the way that works for you. All transactions are processed through secure, industry-standard providers.
        </p>
      </div>

      <div className="overflow-hidden relative">
        <div className="marquee">
          {[...PAYMENT_METHODS, ...PAYMENT_METHODS].map((m, i) => (
            <span key={`${m.name}-${i}`} className="pay-chip" data-testid={`pay-${i}`}>
              <PayIcon method={m} />
              <span className="pay-label">{m.name}</span>
            </span>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#F5F7FA] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#F5F7FA] to-transparent" />
      </div>

      <div className="mt-8 flex items-center justify-center gap-2 text-[#4A4A4A] text-sm">
        <Lock size={14} className="text-[#F37021]" /> Encrypted · PCI-compliant providers
      </div>
    </div>
  </section>
);

const PayIcon = ({ method }) => {
  if (method.slug) {
    return (
      <img
        src={`https://cdn.simpleicons.org/${method.slug}/${method.color}`}
        alt={method.name}
        className="pay-logo"
        loading="lazy"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
    );
  }
  const letter = method.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <span
      className="pay-mono"
      style={{ background: `#${method.fallback}`, color: method.fallback === "B2FCE4" ? "#002B7F" : "#FFFFFF" }}
      aria-hidden
    >
      {letter}
    </span>
  );
};

export const About = () => (
  <section id="about" className="section">
    <div className="container-x grid md:grid-cols-12 gap-10 items-center">
      <div className="md:col-span-6 relative">
        <div className="navy-gradient rounded-3xl p-8 text-white relative overflow-hidden grain">
          <div className="hero-orb" style={{ width: 280, height: 280, background: "#F37021", top: -80, right: -60, opacity: 0.45 }} />
          <span className="eyebrow eyebrow-on-navy relative z-10"><ShieldCheck size={12} /> About us</span>
          <h3 className="font-display text-3xl sm:text-4xl mt-5 relative z-10 leading-tight">
            Independent Administrative Support
          </h3>
          <p className="mt-4 text-white/80 relative z-10">
            Not affiliated with or endorsed by any UK government department or public authority.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4 relative z-10">
            {[
              { k: "Confidential", v: "Service" },
              { k: "UK", v: "Supporting you" },
              { k: "EN · RO · ES", v: "Multilingual" },
            ].map((s) => (
              <div key={s.v} className="bg-white/10 border border-white/15 rounded-xl p-4 backdrop-blur">
                <div className="font-display text-2xl text-[#FF8C42]">{s.k}</div>
                <div className="text-[12px] text-white/80">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="md:col-span-6">
        <span className="eyebrow"><Users size={12} /> Why Marioway</span>
        <h2 className="font-display text-4xl sm:text-5xl mt-4 text-[#002B7F] leading-[1.05]">
          Supporting you in <span className="text-[#F37021]">the UK.</span>
        </h2>
        <p className="mt-4 text-[#4A4A4A] text-base">
          Marioway Benefits Consultancy Ltd provides independent administrative, welfare support, accounting administration, education support and business support services to individuals, families and small businesses across the UK. We help you understand the process, organise your documentation and complete applications — clearly, calmly and confidentially. Outcome decisions remain with the relevant authority.
        </p>
        <div className="mt-6 rounded-2xl border border-[#E6EAF0] bg-[#F5F7FA] p-5" data-testid="about-academic-block">
          <div className="text-[11px] font-bold tracking-[0.16em] uppercase text-[#F37021]">Qualified academic background in Accounting &amp; Financial Management</div>
          <p className="mt-2 text-[14px] text-[#1A1A1A] leading-relaxed">
            Marioway Benefits Consultancy Ltd is led by <span className="font-bold text-[#002B7F]">Florin-Marius Bran</span>, who holds a Bachelor of Science degree in Accounting and Financial Management from Canterbury Christ Church University (awarded May 2026).
          </p>
          <span className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#002B7F] text-white text-[12px] font-semibold px-3 py-1.5" data-testid="about-degree-badge">
            <GraduationCap size={13} className="text-[#FCD116]" /> BSc Accounting &amp; Financial Management · Canterbury Christ Church University · 2026
          </span>
        </div>
        <ul className="mt-6 space-y-3">
          {[
            "Confidential Service",
            "Clear Guidance",
            "Practical Administrative Assistance",
            "Multilingual Support",
          ].map((t) => (
            <li key={t} className="flex items-center gap-3 text-[#1A1A1A]">
              <span className="icon-tile orange" style={{ width: 36, height: 36 }}>
                <CheckCircle2 size={18} />
              </span>
              <span className="font-semibold">{t}</span>
            </li>
          ))}
        </ul>

        <div className="mt-8 flex flex-wrap gap-3">
          <a href="tel:07383513170" className="btn-orange" data-testid="about-cta-call">
            <Phone size={18} /> Call 07383513170
          </a>
          <a href="/#contact" className="btn-ghost" data-testid="about-cta-contact">
            Contact us <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </div>
  </section>
);
